import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigation } from '../../hooks/useNavigation';
import BackgroundShapes from '../common/BackgroundShapes';
import SearchSection from '../SearchSection';
import { SelectedLocation } from '../../types';
import { OnboardingData } from '../../hooks/useOnboarding';
import { userAPI } from '../../services/apiService';
import {
  occupations,
  languages,
  cleanlinessOptions,
  socialLevelOptions,
  workScheduleOptions,
  petOptions
} from '../../constants/formData';

interface ProfileEditPageProps {
  onNavigate: (page: string) => void;
}


const ProfileEditPage: React.FC<ProfileEditPageProps> = ({ onNavigate }) => {
  const { user, token, updateUser } = useAuth();
  const navigation = useNavigation();
  const [step, setStep] = useState(1);
  const [data, setData] = useState<OnboardingData>({
    location: [],
    housingSituation: '',
    budget: '',
    moveInDate: '',
    age: '',
    occupation: '',
    languages: [],
    cleanliness: '',
    socialLevel: '',
    workSchedule: '',
    hasPets: [],
    okWithPets: [],
    bio: ''
  });
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load existing user data and pre-fill forms
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user?.id || !token) {
        setLoadingProfile(false);
        return;
      }

      try {
        setLoadingProfile(true);
        setError(null);
        const response = await userAPI.getProfile(user.id, token);
        
        if (response.success && response.data?.user) {
          const userData = response.data.user;
          
          // Map backend user data to OnboardingData format
          const mappedData: OnboardingData = {
            location: userData.housingInfo?.selectedLocations || [],
            budget: userData.housingInfo?.rentPreferences
              ? `${userData.housingInfo.rentPreferences.minRent}-${userData.housingInfo.rentPreferences.maxRent}`
              : '',
            housingSituation: userData.housingInfo?.housingSituation || '',
            moveInDate: userData.housingInfo?.moveInDate || '',
            age: userData.personalInfo?.age ?? '',
            occupation: userData.occupation || '',
            languages: userData.languages || [],
            cleanliness: userData.lifestyle?.cleanliness || '',
            socialLevel: userData.lifestyle?.socialLevel || '',
            workSchedule: userData.lifestyle?.workSchedule || '',
            hasPets: userData.lifestyle?.hasPets || [],
            okWithPets: userData.lifestyle?.okWithPets || [],
            bio: userData.personalInfo?.bio || ''
          };
          
          setData(mappedData);
        }
      } catch (err: any) {
        console.error('Error loading profile:', err);
        setError(err.message || 'Failed to load profile');
      } finally {
        setLoadingProfile(false);
      }
    };

    loadUserProfile();
  }, [user, token]);

  const updateData = useCallback((updates: Partial<OnboardingData>) => {
    setData(prev => ({ ...prev, ...updates }));
  }, []);

  const nextStep = () => {
    setStep(prev => Math.min(prev + 1, 2));
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleNext = async () => {
    // Validate step 1
    if (step === 1) {
      if (!data.location || data.location.length === 0) {
        alert('Please select at least one location');
        return;
      }
      if (!data.housingSituation) {
        alert('Please select your housing situation');
        return;
      }
      if (!data.budget) {
        alert('Please select your budget range');
        return;
      }
      if (!data.moveInDate) {
        alert('Please select your move-in timeline');
        return;
      }
    }

    // Validate step 2
    if (step === 2) {
      if (!data.occupation) {
        alert('Please select your occupation');
        return;
      }
      if (!data.cleanliness) {
        alert('Please select your cleanliness preference');
        return;
      }
      if (!data.socialLevel) {
        alert('Please select your social preference');
        return;
      }
      if (!data.workSchedule) {
        alert('Please select your work schedule');
        return;
      }
    }

    if (step === 2) {
      // Save changes on final step
      await handleSave();
    } else {
      // Move to next step
      nextStep();
    }
  };

  const handleSave = async () => {
    if (!user?.id || !token) {
      alert('No authentication token found. Please log in again.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Convert form data to backend format
      const selectedLocations = data.location.map(loc => ({
        borough: loc.borough,
        neighborhood: loc.neighborhood,
        id: loc.id || `${loc.neighborhood.toLowerCase().replace(/\s+/g, '-')}-${loc.borough.toLowerCase()}`
      }));

      const budgetParts = data.budget.split('-');
      const minRent = parseInt(budgetParts[0].replace(/\D/g, '') || '1000');
      const maxRent = budgetParts[1] 
        ? parseInt(budgetParts[1].replace(/\D/g, '') || '2000')
        : parseInt(budgetParts[0].replace(/\D/g, '') || '2000');

      // Prepare update payload matching backend structure
      const updates: Record<string, unknown> = {
        housingInfo: {
          selectedLocations,
          rentPreferences: {
            minRent,
            maxRent
          },
          housingSituation: data.housingSituation,
          moveInDate: data.moveInDate || undefined
        },
        occupation: data.occupation,
        languages: data.languages || [],
        lifestyle: {
          cleanliness: data.cleanliness,
          socialLevel: data.socialLevel,
          workSchedule: data.workSchedule,
          hasPets: data.hasPets || [],
          okWithPets: data.okWithPets || []
        }
      };
      if (data.bio !== undefined) {
        updates.bio = data.bio;
      }

      // Update profile using PUT /api/users/:userId
      const response = await userAPI.updateProfile(user.id, updates, token);

      if (response.success) {
        // Update user in auth context
        if (response.data?.user) {
          updateUser({
            profileCompleteness: response.data.profileCompleteness || response.data.user.metadata?.profileCompleteness
          });
        }

        // Navigate back to ProfilePage
        try {
          navigation.goToProfile();
        } catch {
          onNavigate('profile');
        }
      } else {
        setError(response.message || 'Failed to save profile');
      }
    } catch (err: any) {
      console.error('Error saving profile:', err);
      setError(err.message || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  // Helper for handling pet multi-select with "none" logic
  const handlePetToggle = (currentArray: string[], value: string, field: 'hasPets' | 'okWithPets') => {
    if (value === 'none') {
      updateData({ [field]: ['none'] });
    } else {
      const filteredArray = currentArray.filter(item => item !== 'none');
      const newArray = filteredArray.includes(value)
        ? filteredArray.filter(item => item !== value)
        : [...filteredArray, value];
      updateData({ [field]: newArray });
    }
  };

  // Helper for handling regular multi-select
  const handleArrayToggle = (currentArray: string[], value: string, updateFn: (newArray: string[]) => void) => {
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    updateFn(newArray);
  };

  // This updates the onboarding data whenever locations change
  const handleLocationChange = useCallback((locations: SelectedLocation[]) => {
    updateData({ location: locations });
  }, [updateData]);

  const renderStepContent = () => {
    if (loadingProfile) {
      return (
        <div className="text-center py-8">
          <p className="text-coffee opacity-80">Loading profile...</p>
        </div>
      );
    }

    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-coffee mb-2">Where do you want to live?</h2>
              <p className="text-coffee opacity-80">Select up to 5 neighborhoods you'd consider</p>
            </div>

            <SearchSection 
              onSearch={handleLocationChange}
              showSearchButton={false}
              realTimeUpdate={true}
              initialLocations={data.location || []}
            />

            {data.location && data.location.length > 0 && (
              <div className="bg-green-100 border-2 border-green-500 rounded-lg p-3 text-center">
                <p className="text-green-700 text-sm">
                  ✓ {data.location.length} neighborhood{data.location.length > 1 ? 's' : ''} selected
                </p>
              </div>
            )}

            <hr className="border-coffee border-opacity-20" />

            <div className="space-y-4">
              <div>
                <label className="block text-coffee mb-2 font-semibold">Housing Situation</label>
                <select
                  value={data.housingSituation}
                  onChange={(e) => updateData({ housingSituation: e.target.value as any })}
                  className="w-full p-3 rounded-lg bg-white text-coffee border border-coffee focus:border-terracotta focus:outline-none focus:ring-2 focus:ring-terracotta transition-colors"
                >
                  <option value="" className="text-gray-800">Select your situation</option>
                  <option value="looking" className="text-gray-800">Looking for a place together</option>
                  <option value="have-apartment" className="text-gray-800">I have a place, need roommate</option>
                  <option value="flexible" className="text-gray-800">Flexible with either option</option>
                </select>
              </div>

              <div>
                <label className="block text-coffee mb-2 font-semibold">Budget Range *</label>
                <select
                  value={data.budget || ''}
                  onChange={(e) => updateData({ budget: e.target.value })}
                  className="w-full p-3 rounded-lg bg-white text-coffee border border-coffee focus:border-terracotta focus:outline-none focus:ring-2 focus:ring-terracotta transition-colors"
                >
                  <option value="" className="text-gray-800">Select budget range</option>
                  <option value="1000-1500" className="text-gray-800">$1,000 - $1,500/month</option>
                  <option value="1500-2000" className="text-gray-800">$1,500 - $2,000/month</option>
                  <option value="2000-2500" className="text-gray-800">$2,000 - $2,500/month</option>
                  <option value="2500-3000" className="text-gray-800">$2,500 - $3,000/month</option>
                  <option value="3000+" className="text-gray-800">$3,000+/month</option>
                </select>
              </div>

              <div>
                <label className="block text-coffee mb-2 font-semibold">Move-in Month</label>
                {(() => {
                  const now = new Date();
                  const currentYear = now.getFullYear();
                  const currentMonth = now.getMonth() + 1;
                  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
                  const moveInStr = typeof data.moveInDate === 'string' ? data.moveInDate : '';
                  const existingParts = (moveInStr || '').split('-');
                  const selectedYear = existingParts[0] && existingParts[0].length === 4 ? existingParts[0] : '';
                  const selectedMonth = existingParts[1] || '';
                  const startYear = Math.min(2025, currentYear);
                  const yearOptions = Array.from({ length: currentYear + 3 - startYear + 1 }, (_, i) => startYear + i);
                  const getMonthOptions = (year: number) => {
                    const minMonth = year === currentYear ? currentMonth : 1;
                    return monthNames
                      .map((name, idx) => ({ label: name, value: String(idx + 1).padStart(2, '0') }))
                      .filter((_, idx) => idx + 1 >= minMonth);
                  };
                  const handleYearChange = (year: string) => {
                    if (!year) { updateData({ moveInDate: '' }); return; }
                    const y = parseInt(year);
                    const minM = y === currentYear ? currentMonth : 1;
                    const m = selectedMonth && parseInt(selectedMonth) >= minM ? selectedMonth : String(minM).padStart(2, '0');
                    updateData({ moveInDate: `${year}-${m}-01` });
                  };
                  const handleMonthChange = (month: string) => {
                    if (!month || !selectedYear) return;
                    updateData({ moveInDate: `${selectedYear}-${month}-01` });
                  };
                  return (
                    <div className="flex gap-3">
                      <select
                        value={selectedMonth}
                        onChange={(e) => handleMonthChange(e.target.value)}
                        className="flex-1 p-3 rounded-lg bg-white text-coffee border border-coffee focus:border-terracotta focus:outline-none focus:ring-2 focus:ring-terracotta transition-colors"
                      >
                        <option value="" className="text-gray-800">Month</option>
                        {getMonthOptions(parseInt(selectedYear) || currentYear).map(m => (
                          <option key={m.value} value={m.value} className="text-gray-800">{m.label}</option>
                        ))}
                      </select>
                      <select
                        value={selectedYear}
                        onChange={(e) => handleYearChange(e.target.value)}
                        className="w-32 p-3 rounded-lg bg-white text-coffee border border-coffee focus:border-terracotta focus:outline-none focus:ring-2 focus:ring-terracotta transition-colors"
                      >
                        <option value="" className="text-gray-800">Year</option>
                        {yearOptions.map(y => (
                          <option key={y} value={String(y)} className="text-gray-800">{y}</option>
                        ))}
                      </select>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-coffee mb-2">Lifestyle Preferences</h2>
              <p className="text-coffee opacity-80">Help us find your most compatible roommate</p>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-coffee mb-3 font-semibold">What's your occupation? *</label>
                <select
                  value={data.occupation}
                  onChange={(e) => updateData({ occupation: e.target.value })}
                  className="w-full p-3 rounded-lg bg-white text-coffee border border-coffee focus:border-terracotta focus:outline-none focus:ring-2 focus:ring-terracotta transition-colors"
                >
                  {occupations.map((option) => (
                    <option key={option.value} value={option.value} className="text-gray-800">
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-coffee mb-3 font-semibold">What languages do you speak? (Select all that apply)</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {languages.map((lang) => (
                    <button
                      key={lang.value}
                      onClick={() => handleArrayToggle(
                        data.languages,
                        lang.value,
                        (newArray) => updateData({ languages: newArray })
                      )}
                      className={`p-2 rounded-lg text-left transition-colors border-2 ${
                        data.languages.includes(lang.value)
                          ? 'bg-terracotta border-terracotta text-white'
                          : 'bg-white border-coffee text-coffee hover:bg-cream'
                      } text-sm`}
                    >
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-coffee mb-3 font-semibold">How important is cleanliness to you? *</label>
                <div className="grid grid-cols-1 gap-3">
                  {cleanlinessOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => updateData({ cleanliness: option.value })}
                      className={`p-3 rounded-lg text-left transition-colors border-2 ${
                        data.cleanliness === option.value
                          ? 'bg-terracotta border-terracotta text-white'
                          : 'bg-white border-coffee text-coffee hover:bg-cream'
                      }`}
                    >
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-coffee mb-3 font-semibold">What's your social preference? *</label>
                <div className="grid grid-cols-1 gap-3">
                  {socialLevelOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => updateData({ socialLevel: option.value })}
                      className={`p-3 rounded-lg text-left transition-colors border-2 ${
                        data.socialLevel === option.value
                          ? 'bg-terracotta border-terracotta text-white'
                          : 'bg-white border-coffee text-coffee hover:bg-cream'
                      }`}
                    >
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-coffee mb-3 font-semibold">What's your work schedule like? *</label>
                <div className="grid grid-cols-1 gap-3">
                  {workScheduleOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => updateData({ workSchedule: option.value })}
                      className={`p-3 rounded-lg text-left transition-colors border-2 ${
                        data.workSchedule === option.value
                          ? 'bg-terracotta border-terracotta text-white'
                          : 'bg-white border-coffee text-coffee hover:bg-cream'
                      }`}
                    >
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-coffee mb-3 font-semibold">Do you have pets? (Select all that apply)</label>
                <div className="grid grid-cols-4 gap-3">
                  {petOptions.map((pet) => (
                    <button
                      key={pet.value}
                      onClick={() => handlePetToggle(data.hasPets, pet.value, 'hasPets')}
                      className={`p-3 rounded-lg text-center transition-colors border-2 ${
                        data.hasPets.includes(pet.value)
                          ? 'bg-steel border-steel text-white'
                          : 'bg-white border-coffee text-coffee hover:bg-cream'
                      }`}
                    >
                      <span>{pet.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-coffee mb-3 font-semibold">What roommate's pets are you okay with? (Select all that apply)</label>
                <div className="grid grid-cols-4 gap-3">
                  {petOptions.map((pet) => (
                    <button
                      key={pet.value}
                      onClick={() => handlePetToggle(data.okWithPets, pet.value, 'okWithPets')}
                      className={`p-3 rounded-lg text-center transition-colors border-2 ${
                        data.okWithPets.includes(pet.value)
                          ? 'bg-terracotta border-terracotta text-white'
                          : 'bg-white border-coffee text-coffee hover:bg-cream'
                      }`}
                    >
                      <span>{pet.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <hr className="border-coffee border-opacity-20 my-6" />
              <div>
                <label className="block text-coffee mb-3 font-semibold">About Me (optional)</label>
                <p className="text-coffee opacity-80 text-sm mb-2">Add or update your bio</p>
                <textarea
                  value={data.bio || ''}
                  onChange={(e) => updateData({ bio: e.target.value.slice(0, 500) })}
                  placeholder="What should potential roommates know about you?"
                  className="w-full p-3 rounded-lg bg-white text-coffee placeholder-coffee placeholder-opacity-50 border border-coffee focus:border-terracotta focus:outline-none focus:ring-2 focus:ring-terracotta transition-colors min-h-[100px]"
                  maxLength={500}
                  disabled={loading}
                />
                <p className="text-coffee opacity-60 text-xs mt-1">{(data.bio || '').length} / 500</p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-paper relative">
      <BackgroundShapes />
      
      <div className="container mx-auto px-4 py-8 relative z-10">
        <div className="max-w-2xl mx-auto">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-2xl font-bold text-coffee">Edit Profile</h1>
              <div className="flex items-center gap-4">
                <span className="text-coffee opacity-80">Step {step} of 2</span>
                <button
                  onClick={() => {
                    try {
                      navigation.goToProfile();
                    } catch {
                      onNavigate('profile');
                    }
                  }}
                  className="bg-white border-2 border-coffee text-coffee px-4 py-2 rounded-lg hover:bg-cream transition-colors shadow-sm"
                >
                  ← Back to Profile
                </button>
              </div>
            </div>
            <div className="w-full bg-white border border-coffee rounded-full h-2">
              <div 
                className="bg-terracotta h-2 rounded-full transition-all duration-300"
                style={{ width: `${(step / 2) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 border-2 border-red-400 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Step Content */}
          <div className="bg-cream border-2 border-coffee rounded-xl p-8 shadow-md mb-8">
            {renderStepContent()}
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <button
              onClick={prevStep}
              disabled={step === 1}
              className="bg-white border-2 border-coffee text-coffee px-6 py-3 rounded-lg hover:bg-cream transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              ← Previous
            </button>
            
            <button
              onClick={handleNext}
              disabled={loading || loadingProfile}
              className="bg-terracotta text-white px-6 py-3 rounded-lg font-bold hover:bg-[#d89050] transition-all duration-300 disabled:opacity-50 border-2 border-coffee shadow-lg"
            >
              {loading ? 'Saving...' : step === 2 ? 'Save Changes' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileEditPage;

